/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { OpenAPIHousien } from '@/services/requests/core/OpenAPI';
import { request as __request } from '@/services/requests/core/request';

interface FavoritesResponse {
  property_ids: string[];
}

/**
 * Whether this listing is on the signed-in broker's shortlist, and a toggle.
 *
 * The shortlist is per user: the backend keys it by the token's subject, so
 * nothing here passes an identity and one broker never sees another's stars.
 * Distinct from `classification.featured`, which is the agency marking a
 * listing for everyone.
 *
 * The toggle is optimistic — a star that waits for a round trip before
 * filling in feels broken — and rolls back with a message if the write
 * fails.
 *
 * @param agencyId - Agency owning the listing.
 * @param propertyId - The listing being starred.
 * @returns The current state, a toggle, and a saving flag.
 */
/** The ids on the signed-in broker's shortlist, or none when unreadable. */
async function readShortlist(agencyId: string): Promise<string[]> {
  const data = await __request<FavoritesResponse>(OpenAPIHousien, {
    method: 'GET',
    url: '/agencies/{agencyId}/property-favorites',
    path: { agencyId },
  });
  return data?.property_ids ?? [];
}

/** Adds the listing to the shortlist, or takes it off. */
async function writeShortlist(agencyId: string, propertyId: string, on: boolean): Promise<void> {
  await __request(OpenAPIHousien, {
    method: on ? 'PUT' : 'DELETE',
    url: '/agencies/{agencyId}/property-favorites/{propertyId}',
    path: { agencyId, propertyId },
  });
}

/** Whether the property is in the agency's shortlist, with an optimistic toggle that rolls back on failure. */
export function usePropertyFavorite(agencyId: string, propertyId: string) {
  const { t } = useTranslation('features/properties');
  const [favorite, setFavorite] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!agencyId || !propertyId) return;
    let cancelled = false;

    readShortlist(agencyId)
      .then((ids) => {
        if (!cancelled) setFavorite(ids.includes(propertyId));
      })
      // A shortlist that could not be read is not worth a message: the star
      // simply shows as unset, and pressing it still writes.
      .catch(() => undefined);

    return () => { cancelled = true; };
  }, [agencyId, propertyId]);

  const toggle = useCallback(async () => {
    const next = !favorite;
    setFavorite(next);
    setSaving(true);
    try {
      await writeShortlist(agencyId, propertyId, next);
    } catch {
      setFavorite(!next);
      toast.error(t('detailView.favorite.saveError'));
    } finally {
      setSaving(false);
    }
  }, [favorite, agencyId, propertyId, t]);

  return { favorite, toggle, saving };
}
